import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

// Custom hook for scroll-triggered animations
function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.unobserve(el);
        }
      },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isInView };
}

const LandingPageGCC: React.FC = () => {
  const navigate = useNavigate();
  const [isMuted, setIsMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Track ad visit on page load — capture UTM params from URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hasUtm = params.get('utm_source') || params.get('gclid');
    if (hasUtm) {
      const apiUrl = process.env.REACT_APP_API_URL || '';
      fetch(`${apiUrl}/api/ad-visit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          landing_page: '/gcc',
          utm_source: params.get('utm_source') || undefined,
          utm_medium: params.get('utm_medium') || undefined,
          utm_campaign: params.get('utm_campaign') || undefined,
          utm_content: params.get('utm_content') || undefined,
          utm_term: params.get('utm_term') || undefined,
          gclid: params.get('gclid') || undefined,
          referrer: document.referrer || undefined,
        }),
      })
        .then(r => r.json())
        .then(data => {
          if (data.visit_id) {
            localStorage.setItem('ad_visit_id', String(data.visit_id));
          }
        })
        .catch(() => {});
    }
  }, []);

  const goToLogin = useCallback(() => {
    // Fire Google Ads conversion on CTA click
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'conversion', {
        send_to: 'AW-18013772297/FiwJCN68lIocEIm00Y1D',
        value: 1.0,
        currency: 'USD',
      });
    }
    navigate('/login');
  }, [navigate]);

  const toggleMute = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  }, []);

  // Section animation hooks
  const painSection = useInView();
  const featuresSection = useInView();
  const careerSection = useInView();
  const pricingSection = useInView();
  const useCasesSection = useInView();
  const testimonialsSection = useInView();
  const ctaSection = useInView();

  // Testimonials auto-scroll
  const [testimonialOffset, setTestimonialOffset] = useState(0);
  const testimonials = [
    {
      name: 'Ahmed K.',
      role: 'Software Engineer, Dubai',
      avatar: '/testimonials/gcc-1.jpg',
      text: 'I cracked my coding interview at a top Dubai firm using Cubemate\'s interactive tutorials. Being able to run code and see outputs instantly made all the difference in my prep.',
    },
    {
      name: 'Sara M.',
      role: 'CS Student, Riyadh',
      avatar: '/testimonials/gcc-2.jpg',
      text: 'BookMate helped me finally understand data structures and algorithms. I generated a tutorial on trees and graphs, ran every example, and aced my midterm.',
    },
    {
      name: 'Omar H.',
      role: 'Full-Stack Intern, Abu Dhabi',
      avatar: '/testimonials/gcc-3.jpg',
      text: 'The side-by-side language comparison is incredible. I learned TypeScript in days by mapping it to JavaScript concepts I already knew. Saved me weeks of studying.',
    },
    {
      name: 'Fatima A.',
      role: 'Data Analyst, Doha',
      avatar: '/testimonials/gcc-4.jpg',
      text: 'I built three data analysis projects on Cubemate — uploaded CSVs, wrote Python scripts, generated charts. Added them all to my portfolio and landed my current role.',
    },
    {
      name: 'Khalid R.',
      role: 'Backend Developer, Muscat',
      avatar: '/testimonials/gcc-5.jpg',
      text: 'Was stuck in tutorial hell — watching hours of YouTube but couldn\'t solve a single coding challenge. Cubemate forced me to code along and understand every line. Game changer.',
    },
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setTestimonialOffset((prev) => (prev + 1) % testimonials.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const getVisibleTestimonials = () => {
    const visible = [];
    for (let i = 0; i < 3; i++) {
      visible.push(testimonials[(testimonialOffset + i) % testimonials.length]);
    }
    return visible;
  };

  return (
    <div className="min-h-screen bg-gray-950 text-white overflow-x-hidden">
      {/* ── Navigation ── */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-950/80 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <img src="/cubemate-logo.png" alt="Cubemate" className="w-9 h-9 rounded-lg" />
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Cubemate
              </span>
            </div>
            <button
              onClick={goToLogin}
              className="px-5 py-2 text-sm font-semibold rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 transition-all shadow-lg shadow-blue-500/25"
            >
              Start Free
            </button>
          </div>
        </div>
      </nav>

      {/* ── Hero Section ── */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28">
        {/* Background glow */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[120px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-purple-600/20 rounded-full blur-[120px]" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left - Copy */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center px-4 py-1.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-full text-sm font-medium mb-8">
                Built for Developers in the Gulf
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.1] mb-6">
                <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                  Code Smarter.
                </span>{' '}
                Get Hired Faster.
              </h1>

              <p className="text-xl sm:text-2xl text-gray-300 mb-4 font-medium">
                The AI coding platform used by developers across the Gulf.
              </p>

              <p className="text-base sm:text-lg text-gray-400 mb-8 max-w-xl mx-auto lg:mx-0">
                Write, execute, and understand code in 10+ languages. No setup needed.
              </p>

              {/* Pain triggers */}
              <div className="space-y-3 mb-8 text-left max-w-lg mx-auto lg:mx-0">
                <p className="text-gray-400 text-sm sm:text-base italic">
                  "Still copy-pasting code you can't explain?"
                </p>
                <p className="text-gray-400 text-sm sm:text-base italic">
                  "Can't debug without Stack Overflow?"
                </p>
                <p className="text-gray-400 text-sm sm:text-base italic">
                  "Your resume says 'Python' but can you prove it?"
                </p>
                <p className="text-lg font-bold text-white mt-4">
                  Cubemate fixes that.
                </p>
              </div>

              <button
                onClick={goToLogin}
                className="px-10 py-4 text-lg font-bold rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:-translate-y-0.5"
              >
                Start Free Trial
              </button>
              <p className="text-gray-500 text-sm mt-3">7-day free trial. No upfront payment.</p>
            </div>

            {/* Right - Product Video */}
            <div className="relative flex items-center justify-center">
              <div className="relative w-full aspect-video rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-purple-500/10">
                <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-blue-500/30 via-purple-500/30 to-pink-500/30 -z-10 blur-sm" />

                <video
                  ref={videoRef}
                  className="w-full h-full object-cover cursor-pointer"
                  autoPlay
                  muted
                  loop
                  playsInline
                  onClick={toggleMute}
                  title="Cubemate - AI Code Learning for Gulf Developers"
                >
                  <source src="/cubemate-demo.mp4" type="video/mp4" />
                </video>

                {isMuted && (
                  <button
                    onClick={toggleMute}
                    className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2 px-5 py-2.5 bg-black/60 backdrop-blur-sm border border-white/20 rounded-full text-white text-sm font-medium hover:bg-black/80 transition-all animate-pulse"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
                    </svg>
                    Tap to unmute
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Testimonials Section ── */}
      <section className="py-16 lg:py-20 bg-gradient-to-b from-transparent via-blue-950/10 to-transparent">
        <div
          ref={testimonialsSection.ref}
          className={`max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-700 ${
            testimonialsSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">
              Loved by{' '}
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Cubemate users
              </span>
            </h2>
            <p className="text-gray-400 text-lg">Real stories from real people.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {getVisibleTestimonials().map((t, i) => (
              <div
                key={`${testimonialOffset}-${i}`}
                className="p-6 rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.03] to-transparent hover:border-white/20 transition-all duration-500 animate-fade-in"
              >
                <div className="flex items-center gap-4 mb-4">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-12 h-12 rounded-full object-cover border-2 border-white/10"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(t.name)}&background=10b981&color=fff&size=96`;
                    }}
                  />
                  <div>
                    <p className="text-white font-semibold text-sm">{t.name}</p>
                    <p className="text-gray-500 text-xs">{t.role}</p>
                  </div>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed italic">
                  "{t.text}"
                </p>
              </div>
            ))}
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setTestimonialOffset(i)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  i === testimonialOffset ? 'bg-blue-400 w-6' : 'bg-gray-600 hover:bg-gray-500'
                }`}
                aria-label={`Testimonial group ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── Pain to Solution ── */}
      <section className="py-20 lg:py-28">
        <div
          ref={painSection.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-700 ${
            painSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">
              Stop Watching.{' '}
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Start Building.
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                ),
                title: 'Stop watching, start doing',
                desc: 'No more 3-hour tutorials. Get hands-on with executable code in minutes.',
                gradient: 'from-blue-500/20 to-blue-600/5',
                border: 'border-blue-500/20 hover:border-blue-400/40',
              },
              {
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                ),
                title: 'Understand code, don\'t just copy it',
                desc: 'Cubemate explains every line. You learn to think, not just paste.',
                gradient: 'from-purple-500/20 to-purple-600/5',
                border: 'border-purple-500/20 hover:border-purple-400/40',
              },
              {
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                ),
                title: 'Build projects you can show in interviews',
                desc: 'Portfolio-ready outputs. Not toy examples. Real projects that impress recruiters.',
                gradient: 'from-pink-500/20 to-pink-600/5',
                border: 'border-pink-500/20 hover:border-pink-400/40',
              },
            ].map((card, i) => (
              <div
                key={i}
                className={`bg-gradient-to-br ${card.gradient} border ${card.border} rounded-2xl p-8 transition-all duration-300 hover:shadow-lg hover:shadow-white/5 hover:-translate-y-1`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className="text-blue-400 mb-5">{card.icon}</div>
                <h3 className="text-lg font-bold text-white mb-3">{card.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="py-20 lg:py-28 bg-gradient-to-b from-transparent via-blue-950/10 to-transparent">
        <div
          ref={featuresSection.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-700 ${
            featuresSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-14">
            <div className="inline-flex items-center px-4 py-1.5 bg-blue-500/10 border border-blue-500/20 text-blue-400 rounded-full text-sm font-medium mb-6">
              What You Get
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">
              Everything You Need to{' '}
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Code & Learn
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                ),
                title: 'Execute Code Instantly',
                desc: '10+ languages. No setup. No installations. Write and run Python, Java, C++, JavaScript, and more in your browser.',
                gradient: 'from-blue-500/20 to-blue-600/5',
                border: 'border-blue-500/20 hover:border-blue-400/40',
                color: 'text-blue-400',
              },
              {
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                ),
                title: 'BookMate Tutorials',
                desc: 'AI-generated interactive tutorials. Learn by executing code, not reading docs. Side-by-side dual language comparison.',
                gradient: 'from-purple-500/20 to-purple-600/5',
                border: 'border-purple-500/20 hover:border-purple-400/40',
                color: 'text-purple-400',
              },
              {
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                ),
                title: 'Data & Workflow',
                desc: 'Upload CSVs, generate charts, build data projects. Real outputs you can download and use for assignments.',
                gradient: 'from-pink-500/20 to-pink-600/5',
                border: 'border-pink-500/20 hover:border-pink-400/40',
                color: 'text-pink-400',
              },
            ].map((card, i) => (
              <div
                key={i}
                className={`bg-gradient-to-br ${card.gradient} border ${card.border} rounded-2xl p-8 transition-all duration-300 hover:shadow-lg hover:shadow-white/5 hover:-translate-y-1`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className={`${card.color} mb-5`}>{card.icon}</div>
                <h3 className="text-lg font-bold text-white mb-3">{card.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Career Signals ── */}
      <section className="py-20 lg:py-28">
        <div
          ref={careerSection.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-700 ${
            careerSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">
              Signals That{' '}
              <span className="bg-gradient-to-r from-green-400 to-blue-400 bg-clip-text text-transparent">
                Get You Hired
              </span>
            </h2>
            <p className="text-gray-400 text-lg max-w-xl mx-auto">
              Interviewers don't care what you watched. They care what you built.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {[
              {
                icon: (
                  <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                  </svg>
                ),
                title: 'Portfolio-ready code',
                desc: 'Every project you build is yours. Clean, documented, ready to show.',
                color: 'text-green-400',
                bg: 'bg-green-500/10 border-green-500/20',
              },
              {
                icon: (
                  <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                ),
                title: 'Downloadable project outputs',
                desc: 'Charts, data files, code bundles. Download and submit anywhere.',
                color: 'text-blue-400',
                bg: 'bg-blue-500/10 border-blue-500/20',
              },
              {
                icon: (
                  <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                ),
                title: 'Skills interviewers actually test',
                desc: 'DSA, system design, debugging, code explanation. Practice what matters.',
                color: 'text-yellow-400',
                bg: 'bg-yellow-500/10 border-yellow-500/20',
              },
            ].map((card, i) => (
              <div
                key={i}
                className={`${card.bg} border rounded-2xl p-8 text-center transition-all duration-300 hover:shadow-lg hover:shadow-white/5 hover:-translate-y-1`}
                style={{ transitionDelay: `${i * 100}ms` }}
              >
                <div className={`${card.color} flex justify-center mb-5`}>{card.icon}</div>
                <h3 className="text-lg font-bold text-white mb-3">{card.title}</h3>
                <p className="text-gray-400 text-sm leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing Hint ── */}
      <section className="py-16 lg:py-20">
        <div
          ref={pricingSection.ref}
          className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-700 ${
            pricingSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="bg-gradient-to-br from-blue-500/10 via-purple-500/10 to-pink-500/10 border border-white/10 rounded-2xl p-10 sm:p-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">
              Start free.{' '}
              <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Pro from $15/month.
              </span>
            </h2>
            <p className="text-gray-400 text-lg mb-2">
              7-day free trial. Cancel anytime. No hidden charges.
            </p>
            <p className="text-gray-500 text-sm mb-8">
              Save up to 30% with yearly plans.
            </p>
            <button
              onClick={goToLogin}
              className="px-10 py-4 text-lg font-bold rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:-translate-y-0.5"
            >
              Start Free Trial
            </button>
            <p className="text-gray-500 text-sm mt-4">7-day free trial. No upfront payment.</p>
          </div>
        </div>
      </section>

      {/* ── Use Cases ── */}
      <section className="py-20 lg:py-28 bg-gradient-to-b from-transparent via-purple-950/10 to-transparent">
        <div
          ref={useCasesSection.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-700 ${
            useCasesSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-4">
              Built for{' '}
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                How Students Actually Learn
              </span>
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {[
              {
                title: 'Interview Prep',
                desc: 'Practice coding problems with instant execution and explanations.',
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                ),
              },
              {
                title: 'Tech Job Market',
                desc: 'Stand out in the Gulf\'s competitive tech market with real skills.',
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                ),
              },
              {
                title: 'Learn DSA',
                desc: 'Arrays, trees, graphs, DP. Understand patterns, not just solutions.',
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
                  </svg>
                ),
              },
              {
                title: 'Switch Languages',
                desc: 'Know Java? Learn Python side-by-side with BookMate dual view.',
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                ),
              },
              {
                title: 'Build Projects',
                desc: 'Data analysis, web scrapers, APIs. Real projects, real outputs.',
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                  </svg>
                ),
              },
              {
                title: 'Upskill Fast',
                desc: 'Learn new languages and frameworks quickly. Stay ahead in your career.',
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 14l9-5-9-5-9 5 9 5zm0 0l6.16-3.422a12.083 12.083 0 01.665 6.479A11.952 11.952 0 0012 20.055a11.952 11.952 0 00-6.824-2.998 12.078 12.078 0 01.665-6.479L12 14z" />
                  </svg>
                ),
              },
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-4 p-5 rounded-xl bg-white/[0.03] border border-white/5 hover:border-white/10 transition-all duration-300 hover:-translate-y-0.5"
                style={{ transitionDelay: `${i * 50}ms` }}
              >
                <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/20 to-purple-500/20 flex items-center justify-center text-blue-400">
                  {item.icon}
                </div>
                <div>
                  <h3 className="text-base font-bold text-white mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-20 lg:py-28">
        <div
          ref={ctaSection.ref}
          className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-700 ${
            ctaSection.isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="relative">
            {/* Background glow */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-purple-600/15 rounded-full blur-[100px]" />
            </div>

            <div className="relative">
              <h2 className="text-4xl sm:text-5xl font-extrabold mb-6">
                Your next tech role{' '}
                <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
                  starts here.
                </span>
              </h2>
              <p className="text-gray-400 text-lg mb-10 max-w-xl mx-auto">
                Stop preparing. Start proving. Code it, run it, own it.
              </p>
              <button
                onClick={goToLogin}
                className="px-12 py-4 text-lg font-bold rounded-xl bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-600 hover:to-purple-600 transition-all shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 hover:-translate-y-0.5"
              >
                Start Free Trial
              </button>
              <p className="text-gray-500 text-sm mt-4">Free for 7 days. Pro from $15/month.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-white/10 py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center space-x-3">
              <img src="/cubemate-logo.png" alt="Cubemate" className="w-7 h-7 rounded-lg" />
              <span className="text-sm font-semibold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                Cubemate
              </span>
            </div>
            <div className="flex items-center space-x-6 text-sm text-gray-500">
              <a href="/privacy" className="hover:text-gray-300 transition-colors">Privacy</a>
              <a href="/terms" className="hover:text-gray-300 transition-colors">Terms</a>
            </div>
            <p className="text-xs text-gray-600">
              &copy; {new Date().getFullYear()} Cubemate. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPageGCC;
